/**
 * FoodMenuPage.jsx
 * Main food menu page showing day-wise meal cards
 * Design: Elegant, wedding-friendly meal planning experience
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  UtensilsCrossed,
  Coffee,
  Sun,
  Moon,
  Sunrise,
  Wine,
  ChevronRight,
  CheckCircle2,
  Clock,
  Loader2,
  Users,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import CustomerLayout from "./CustomerLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer`;

// Meal type icons and colors
const MEAL_CONFIG = {
  welcome_drink: { 
    icon: Wine, 
    color: "from-[#D4A5A5] to-[#C49393]",
    bgColor: "bg-[#FDF5F5]"
  },
  breakfast: { 
    icon: Sunrise, 
    color: "from-[#E8C87C] to-[#D4B466]",
    bgColor: "bg-[#FFFBF2]"
  },
  lunch: { 
    icon: Sun, 
    color: "from-[#A8C5DA] to-[#8FB3C9]",
    bgColor: "bg-[#F2F7FA]"
  },
  high_tea: { 
    icon: Coffee, 
    color: "from-[#C5D5C5] to-[#A8C5A8]",
    bgColor: "bg-[#F5FAF5]"
  },
  dinner: { 
    icon: Moon, 
    color: "from-[#B8A5C5] to-[#A090B0]",
    bgColor: "bg-[#F8F5FA]"
  }
};

const STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "text-gray-400", bgColor: "bg-gray-50" },
  in_progress: { label: "In Progress", color: "text-amber-600", bgColor: "bg-amber-50" },
  submitted: { label: "Submitted", color: "text-blue-600", bgColor: "bg-blue-50" },
  under_review: { label: "Under Review", color: "text-purple-600", bgColor: "bg-purple-50" },
  finalized: { label: "Finalized", color: "text-green-600", bgColor: "bg-green-50" }
};

const FoodMenuPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mealsData, setMealsData] = useState(null);

  // Get stored data
  const customer = JSON.parse(localStorage.getItem("customer_data") || "{}");
  const wedding = JSON.parse(localStorage.getItem("wedding_data") || "{}");

  useEffect(() => {
    const fetchMeals = async () => {
      const token = localStorage.getItem("customer_token");
      if (!token) {
        navigate("/my-wedding");
        return;
      }

      try {
        const response = await axios.get(`${API}/food/meals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMealsData(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem("customer_token");
          navigate("/my-wedding");
        } else {
          toast.error("Unable to load meal data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMeals();
  }, [navigate]);

  // Calculate progress
  const calculateProgress = () => {
    if (!mealsData) return { completed: 0, total: 0, percent: 0 };
    
    let completed = 0;
    let total = 0;
    
    mealsData.days.forEach(day => {
      day.meals.forEach(meal => {
        if (meal.included) {
          total++;
          if (meal.status === "submitted" || meal.status === "under_review" || meal.status === "finalized") {
            completed++;
          }
        }
      });
    });
    
    return {
      completed,
      total,
      percent: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  const progress = calculateProgress();

  if (loading) {
    return (
      <CustomerLayout customer={customer} wedding={wedding}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#C9A87C] mx-auto mb-4" />
            <p className="text-[#8B7355]">Loading your meal plans...</p>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout customer={customer} wedding={wedding}>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A87C]/10 rounded-full mb-4">
            <UtensilsCrossed className="w-4 h-4 text-[#C9A87C]" />
            <span className="text-sm font-medium text-[#8B6914]">Food & Menu</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif text-[#5C4A3D] mb-2">
            Curate Your Celebration Menu
          </h1>
          <p className="text-[#8B7355] max-w-xl mx-auto">
            Select dishes for each meal to create a memorable dining experience for your guests.
          </p>
        </div>

        {/* Guest Count & Progress */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5] flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#C9A87C]/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#C9A87C]" />
            </div>
            <div>
              <p className="text-xs text-[#8B7355] uppercase tracking-wide">Guest Count</p>
              <p className="text-2xl font-semibold text-[#5C4A3D]">
                {mealsData?.guest_count || 100} <span className="text-sm font-normal">guests</span>
              </p>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#8B7355]">Menu Progress</p>
              <p className="text-sm font-medium text-[#C9A87C]">{progress.percent}%</p>
            </div>
            <Progress value={progress.percent} className="h-2 bg-[#F5EDE8]" />
            <p className="text-xs text-[#8B7355] mt-2">
              {progress.completed} of {progress.total} menus completed
            </p>
          </div>
        </div>

        {/* Day-wise Meal Cards */}
        {mealsData?.days.map((day) => (
          <section key={day.day} className="space-y-4">
            {/* Day Header */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A87C] to-[#B08968] flex items-center justify-center text-white font-semibold">
                {day.day}
              </div>
              <h2 className="text-xl font-semibold text-[#5C4A3D]">{day.label}</h2>
            </div>

            {/* Meal Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {day.meals.map((meal) => {
                const config = MEAL_CONFIG[meal.type] || MEAL_CONFIG.lunch;
                const statusConfig = STATUS_CONFIG[meal.status] || STATUS_CONFIG.not_started;
                const MealIcon = config.icon;

                // Welcome drink is included, show different card
                if (meal.type === "welcome_drink" && meal.included) {
                  return (
                    <div
                      key={meal.type}
                      className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5]"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
                          <MealIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-medium text-[#5C4A3D]">{meal.label}</h3>
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Included
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-[#8B7355]">
                        Complimentary welcome refreshments for all guests
                      </p>
                    </div>
                  );
                }

                // Skip if not included
                if (!meal.included) {
                  return null;
                }

                return (
                  <button
                    key={meal.type}
                    onClick={() => navigate(`/my-wedding/food-menu/${day.day}/${meal.type}`)}
                    className="group bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-[#E8DED5] hover:border-[#C9A87C]/50 hover:shadow-lg transition-all duration-300 text-left"
                    data-testid={`meal-card-${day.day}-${meal.type}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
                        <MealIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className={`px-2 py-1 rounded-full ${statusConfig.bgColor}`}>
                        <span className={`text-xs font-medium ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>
                    
                    <h3 className="font-medium text-[#5C4A3D] mb-1 group-hover:text-[#8B6914] transition-colors">
                      {meal.label}
                    </h3>
                    <p className="text-sm text-[#8B7355] mb-3">
                      for {meal.guest_count} pax
                    </p>
                    
                    {meal.package_name && (
                      <p className="text-xs text-[#C9A87C] mb-2">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        {meal.package_name} Package
                      </p>
                    )}
                    
                    <div className="flex items-center gap-1 text-[#C9A87C] text-sm font-medium">
                      <span>
                        {meal.status === "not_started" ? "Finalize Menu" : 
                         meal.status === "finalized" ? "View Menu" : "Continue"}
                      </span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        {/* Help Section */}
        <section className="bg-gradient-to-r from-[#C9A87C]/10 to-[#D4B896]/10 rounded-2xl p-6 border border-[#D4C5B0]/30">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
              <UtensilsCrossed className="w-5 h-5 text-[#C9A87C]" />
            </div>
            <div>
              <p className="text-[#5C4A3D] font-medium mb-1">Our culinary team is here to help</p>
              <p className="text-sm text-[#8B7355]">
                Once you submit your menu selections, our chef will review and confirm the dishes. 
                Feel free to add special dietary requirements or preferences.
              </p>
            </div>
          </div>
        </section>
      </div>
    </CustomerLayout>
  );
};

export default FoodMenuPage;
